import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Search, Info } from "lucide-react";
import { fetchBilanci } from "../api";

const AREAS = [
  { key: "sito_investor_relations", label: "Sito IR", max: 20 },
  { key: "assemblee_azionisti", label: "Assemblee", max: 25 },
  { key: "comunicazioni_mercato", label: "Comunicazioni", max: 20 },
  { key: "dialogo_azionisti", label: "Dialogo azionisti", max: 20 },
  { key: "qualita_informazione", label: "Qualità info", max: 15 },
];

const barColor = (ratio) => (ratio >= 0.7 ? "#2e7d32" : ratio >= 0.4 ? "#f5a623" : "#d32f2f");

const fasciaOf = (score) => (score >= 71 ? "Alta" : score >= 41 ? "Media" : "Bassa");

const TrasparenzaEGM = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sector, setSector] = useState("Tutti");
  const [fascia, setFascia] = useState("Tutte");
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    fetchBilanci()
      .then(setItems)
      .catch(() => setItems([]))
      .finally(() => {
        setLoading(false);
        setTimeout(() => setMounted(true), 100);
      });
  }, []);

  const sectors = useMemo(
    () => ["Tutti", ...Array.from(new Set(items.map((i) => i.settore)))],
    [items]
  );

  const scored = useMemo(() => {
    return items
      .filter((i) => i.trasparenza && i.stato !== "in_lavorazione")
      .sort((a, b) => (b.trasparenza.punteggio_totale || 0) - (a.trasparenza.punteggio_totale || 0))
      .filter((i) => {
        const s = i.trasparenza.punteggio_totale || 0;
        const okSector = sector === "Tutti" || i.settore === sector;
        const okFascia = fascia === "Tutte" || fasciaOf(s) === fascia;
        return okSector && okFascia;
      });
  }, [items, sector, fascia]);

  const inLav = useMemo(() => {
    if (fascia !== "Tutte") return [];
    return items.filter(
      (i) => i.stato === "in_lavorazione" && (sector === "Tutti" || i.settore === sector)
    );
  }, [items, sector, fascia]);

  const rows = scored;

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 fade-in">
      <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
        <Link to="/" className="hover:text-sq-orange">Home</Link>
        <span>/</span>
        <Link to="/bilancio-60-secondi" className="hover:text-sq-orange">Bilancio in 60 secondi</Link>
        <span>/</span>
        <span className="text-sq-navy font-medium">Classifica Trasparenza</span>
      </div>

      <h1 className="font-serif-display text-4xl text-sq-navy mb-2">
        🔍 Chi comunica meglio con gli azionisti? — Classifica Trasparenza EGM
      </h1>
      <p className="text-gray-600 max-w-3xl">
        Valutazione indipendente e oggettiva della comunicazione delle PMI quotate su Euronext Growth Milan.
      </p>
      <div className="w-16 h-1 bg-sq-orange mt-3 mb-8" />

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wide">Settore</label>
          <select value={sector} onChange={(e) => setSector(e.target.value)}
            className="border border-sq rounded-sm px-3 py-2.5 text-sm outline-none bg-white min-w-[220px] focus:border-sq-orange">
            {sectors.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wide">Fascia di punteggio</label>
          <select value={fascia} onChange={(e) => setFascia(e.target.value)}
            className="border border-sq rounded-sm px-3 py-2.5 text-sm outline-none bg-white min-w-[200px] focus:border-sq-orange">
            <option value="Tutte">Tutte</option>
            <option value="Alta">Alta (71-100)</option>
            <option value="Media">Media (41-70)</option>
            <option value="Bassa">Bassa (0-40)</option>
          </select>
        </div>
      </div>

      {loading ? (
        <p className="text-gray-400">Caricamento...</p>
      ) : rows.length === 0 && inLav.length === 0 ? (
        <p className="text-gray-500">Nessuna società in questa selezione.</p>
      ) : (
        <div className="border-2 border-[#1f4680] rounded-lg overflow-hidden bg-white">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" style={{ minWidth: 900 }}>
              <thead>
                <tr className="bg-sq-navy text-white text-left">
                  <th className="px-3 py-3 font-semibold w-10">#</th>
                  <th className="px-3 py-3 font-semibold">Società</th>
                  <th className="px-3 py-3 font-semibold w-48">Punteggio totale</th>
                  {AREAS.map((a) => (
                    <th key={a.key} className="px-3 py-3 font-semibold text-xs">{a.label}<br /><span className="text-[10px] font-normal text-gray-300">/{a.max}</span></th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[#e6e8ec]">
                {rows.map((r, idx) => {
                  const t = r.trasparenza || {};
                  const total = t.punteggio_totale || 0;
                  const totRatio = total / 100;
                  return (
                    <tr key={r.id} className="hover:bg-sq-light align-middle">
                      <td className="px-3 py-3 font-bold text-gray-400">{idx + 1}</td>
                      <td className="px-3 py-3">
                        <Link to={`/bilancio-60-secondi/${r.ticker}`} className="font-semibold text-sq-navy hover:text-sq-orange">
                          {r.societa}
                        </Link>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span className="text-[10px] bg-sq-orange text-white font-bold px-1.5 rounded">EGM</span>
                          <span className="text-xs text-gray-400">{r.ticker} · {r.settore}</span>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-gray-100 rounded-full h-3 overflow-hidden min-w-[80px]">
                            <div className="h-full rounded-full transition-all duration-1000 ease-out"
                              style={{ width: mounted ? `${total}%` : "0%", background: barColor(totRatio) }} />
                          </div>
                          <span className="font-extrabold text-sq-navy w-14 text-right">{total}/100</span>
                        </div>
                      </td>
                      {AREAS.map((a) => {
                        const area = (t.aree || {})[a.key] || { punteggio: 0, max: a.max };
                        const ratio = area.max ? area.punteggio / area.max : 0;
                        return (
                          <td key={a.key} className="px-3 py-3">
                            <div className="bg-gray-100 rounded-full h-2.5 overflow-hidden">
                              <div className="h-full rounded-full transition-all duration-1000 ease-out"
                                style={{ width: mounted ? `${ratio * 100}%` : "0%", background: barColor(ratio) }} />
                            </div>
                            <span className="text-[11px] text-gray-500 mt-1 block">{area.punteggio}/{area.max}</span>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
                {inLav.map((r) => (
                  <tr key={r.id} className="bg-gray-50 text-gray-400">
                    <td className="px-3 py-3">—</td>
                    <td className="px-3 py-3">
                      <span className="font-semibold text-gray-500">{r.societa}</span>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className="text-[10px] bg-gray-400 text-white font-bold px-1.5 rounded">EGM</span>
                        <span className="text-xs text-gray-400">{r.ticker} · {r.settore}</span>
                      </div>
                    </td>
                    <td className="px-3 py-3 italic text-sm" colSpan={AREAS.length + 1}>
                      Valutazione in corso
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Methodology */}
      <div className="mt-10 bg-sq-light border border-sq rounded-lg p-6">
        <h2 className="font-serif-display text-2xl text-sq-navy flex items-center gap-2 mb-3">
          <Info size={20} className="text-sq-orange" /> Metodologia di valutazione
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Ogni società quotata su Euronext Growth Milan è valutata su <strong>100 punti</strong>,
          suddivisi in cinque aree. Il punteggio misura la qualità e la trasparenza della
          comunicazione verso gli azionisti, sulla base di criteri oggettivi verificabili.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {[
            { t: "Sito Investor Relations", m: 20, d: "Sezione IR dedicata, bilanci e presentazioni scaricabili, sito in inglese." },
            { t: "Assemblee azionisti", m: 25, d: "Convocazione nei tempi, domande pre-assemblea, risposte e verbale pubblicati." },
            { t: "Comunicazioni al mercato", m: 20, d: "Comunicati trimestrali, commento del management, investor presentation, contatto IR." },
            { t: "Dialogo con azionisti", m: 20, d: "Risposta email IR entro 5 giorni, roadshow/investor day, presenza su piattaforme IR." },
            { t: "Qualità dell'informazione", m: 15, d: "KPI operativi, guidance futura, analisi dettagliata dei rischi." },
          ].map((x) => (
            <div key={x.t} className="bg-white border border-sq rounded-md p-4">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-sq-navy text-sm">{x.t}</span>
                <span className="text-xs font-bold text-sq-orange">{x.m} pt</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">{x.d}</p>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-4 mt-4 text-xs text-gray-600">
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full inline-block" style={{ background: "#2e7d32" }} /> Alto (≥70%)</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full inline-block" style={{ background: "#f5a623" }} /> Medio (40-69%)</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full inline-block" style={{ background: "#d32f2f" }} /> Basso (&lt;40%)</span>
        </div>
      </div>

      <p className="text-xs text-gray-500 mt-6 italic">
        Valutazione aggiornata periodicamente. Basata su criteri oggettivi e verificabili da chiunque.
      </p>
    </div>
  );
};

export default TrasparenzaEGM;
